import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  pageDestinationId,
  parseDestinationId,
  type DestinationCap,
  type DestinationDto,
  type DestinationFeature,
  type DestinationNetwork,
} from '@mpr/shared';

import { api } from './api.js';

export { pageDestinationId };

/** Order networks appear in. A network not listed here sorts after these. */
const NETWORK_ORDER: DestinationNetwork[] = ['FACEBOOK', 'INSTAGRAM', 'LINKEDIN'];

export interface DestinationEntry {
  dest: DestinationDto;
  cap: DestinationCap;
}

export interface DestinationGroup {
  network: DestinationNetwork;
  usable: DestinationEntry[];
  unavailable: DestinationEntry[];
}

export interface DestinationView {
  groups: DestinationGroup[];
  /** Networks the account has that do not offer this feature at all. */
  unsupported: DestinationNetwork[];
  total: number;
}

/**
 * What a screen offers for one feature: grouped by network, with entries that
 * cannot be used kept apart (and their reason attached) instead of vanishing.
 * Pure, so every picker applies the same rule.
 */
export function viewDestinations(all: DestinationDto[], feature: DestinationFeature): DestinationView {
  const groups = new Map<DestinationNetwork, DestinationGroup>();
  const present = new Set<DestinationNetwork>();
  const supported = new Set<DestinationNetwork>();

  for (const dest of all) {
    present.add(dest.network);
    const cap = dest.caps[feature];
    if (!cap) continue;
    supported.add(dest.network);

    const group = groups.get(dest.network) ?? {
      network: dest.network,
      usable: [],
      unavailable: [],
    };
    (cap.ok ? group.usable : group.unavailable).push({ dest, cap });
    groups.set(dest.network, group);
  }

  const rank = (network: DestinationNetwork) => {
    const index = NETWORK_ORDER.indexOf(network);
    return index === -1 ? NETWORK_ORDER.length : index;
  };

  return {
    groups: [...groups.values()].sort((a, b) => rank(a.network) - rank(b.network)),
    unsupported: [...present].filter((network) => !supported.has(network)),
    total: [...groups.values()].reduce((sum, g) => sum + g.usable.length + g.unavailable.length, 0),
  };
}

export function useDestinations(feature: DestinationFeature) {
  const query = useQuery({
    queryKey: ['destinations'],
    queryFn: api.destinations,
    staleTime: 30_000,
  });
  const view = useMemo(() => viewDestinations(query.data?.destinations ?? [], feature), [
    query.data,
    feature,
  ]);
  return { query, view, all: query.data?.destinations ?? [] };
}

/** The Page row id inside a destination id, or '' for none / not a Page. */
export function pageIdOf(destinationId: string): string {
  const parsed = parseDestinationId(destinationId);
  return parsed?.kind === 'page' ? parsed.id : '';
}
